import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';

/** A single selectable entry in a {@link Dropdown} menu. */
export interface DropdownItem {
  /** Stable key (also used as the React key). */
  key: string;
  /** Visible label. */
  label: string;
  /** Optional leading iconify icon (e.g. `heroicons:archive-box`). */
  icon?: string;
  /** Invoked when the item is chosen (the menu closes first). */
  onSelect: () => void;
  /** `danger` tints the item red for destructive actions. */
  tone?: 'default' | 'danger';
  /** Greys out and disables selection. */
  disabled?: boolean;
  /**
   * For single-choice menus (a language, a sort order): marks the item as a
   * `menuitemradio` with `aria-checked`. Set it on EVERY item of such a menu
   * (true/false) so the check column is reserved and the labels line up.
   */
  checked?: boolean;
}

export interface DropdownProps {
  /** Accessible label for the trigger button (and the menu). */
  label: string;
  /** The menu entries. */
  items: DropdownItem[];
  /** Which trigger edge the menu aligns to (default `right`). */
  align?: 'left' | 'right';
  /** Override the default kebab trigger glyph. */
  triggerIcon?: string;
  /** Render custom trigger contents instead of the default icon. */
  children?: ReactNode;
  /** Shown above the items: who is signed in, what the menu is about. */
  header?: ReactNode;
  /** Extra classes for the trigger button. */
  className?: string;
}

/** Menu width (px); also used to clamp the menu within the viewport. */
const MENU_WIDTH = 224; // == Tailwind w-56

/**
 * A small, generic actions menu: a trigger button opens a `role="menu"` of items.
 *
 * Closes on select, Escape, and outside-click; supports arrow-key / Home / End
 * navigation with focus management. The default trigger is a `heroicons`
 * ellipsis (kebab) with a ≥40px tap target; pass `children` to customise it.
 *
 * The menu renders in a portal at fixed, trigger-anchored coordinates so it
 * escapes any `overflow`/stacking-context ancestor (e.g. the conversation
 * drawer) that would otherwise clip it.
 */
export function Dropdown({
  label,
  items,
  align = 'right',
  triggerIcon = 'heroicons:ellipsis-vertical',
  children,
  header,
  className = '',
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  // How the menu was opened decides where focus lands. A click (`detail > 0`) is a
  // pointer: the menu itself takes focus, so no item lights up as if chosen and the
  // arrow keys still work from it. Enter or Space on the trigger (`detail === 0`)
  // is a keyboard: the first item takes focus, as a menu is expected to.
  const openedByKeyboard = useRef(false);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }
    const reposition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const left =
        align === 'right'
          ? Math.max(8, rect.right - MENU_WIDTH)
          : Math.min(rect.left, window.innerWidth - MENU_WIDTH - 8);
      // Open downwards by default; flip above the trigger when the menu would
      // not fit below it (a trigger in a footer, or near the bottom of a phone
      // screen), so the items stay reachable instead of running off-screen.
      const menuHeight = menuRef.current?.offsetHeight ?? 0;
      const fitsBelow = rect.bottom + 8 + menuHeight <= window.innerHeight - 8;
      const top = fitsBelow || menuHeight === 0 ? rect.bottom + 8 : Math.max(8, rect.top - 8 - menuHeight);
      setPosition({ top, left });
    };
    reposition();
    // The menu only mounts once a position exists, so measure it again on the
    // next frame (still before paint) — that is when the flip can be decided.
    const frame = requestAnimationFrame(reposition);
    window.addEventListener('resize', reposition);
    // Capture phase so we also catch scrolls on nested scroll containers.
    window.addEventListener('scroll', reposition, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, align]);

  // Close on outside click (the portaled menu lives outside the container).
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inTrigger && !inMenu) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // Move focus into the portaled menu once it is mounted — exactly once per open,
  // so a scroll/resize reposition never re-grabs focus.
  const focusedOnOpen = useRef(false);
  useEffect(() => {
    if (!open) {
      focusedOnOpen.current = false;
      return;
    }
    if (!position || focusedOnOpen.current) {
      return;
    }
    focusedOnOpen.current = true;
    if (openedByKeyboard.current) {
      const firstEnabled = items.findIndex((item) => !item.disabled);
      itemRefs.current[Math.max(0, firstEnabled)]?.focus();
    } else {
      menuRef.current?.focus();
    }
  }, [open, position, items]);

  function handleSelect(item: DropdownItem, byKeyboard: boolean) {
    if (item.disabled) {
      return;
    }
    setOpen(false);
    // Only a keyboard user needs the focus back on the trigger; after a click the
    // ring it would draw there reads as a leftover, not as help.
    if (byKeyboard) {
      triggerRef.current?.focus();
    }
    item.onSelect();
  }

  function onMenuKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const elements = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
    const index = elements.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      elements[(index + 1) % elements.length]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      // From the menu itself (no item focused yet) the way up is the last item.
      elements[index < 0 ? elements.length - 1 : (index - 1 + elements.length) % elements.length]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      elements[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      elements[elements.length - 1]?.focus();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      // Stop the key here. Without this it keeps bubbling to whatever dialog the
      // menu is rendered inside, which closes the dialog too — so dismissing a
      // menu tore down the whole drawer and dropped focus on <body>.
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    }
  }

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={(event) => {
          openedByKeyboard.current = event.detail === 0;
          setOpen((value) => !value);
        }}
        className={[
          'inline-flex min-h-[40px] min-w-[40px] cursor-pointer items-center justify-center rounded-lg border border-[var(--color-border-control)] bg-[var(--color-surface)] px-2 text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-text-secondary)] hover:text-[var(--color-text)] focus:outline-hidden focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-1',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {children ?? <Icon icon={triggerIcon} className="h-5 w-5" aria-hidden="true" />}
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            style={{ top: position.top, left: position.left, width: MENU_WIDTH } as CSSProperties}
            className="fixed z-[100] rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-1.5 shadow-sm focus:outline-hidden"
            role="menu"
            aria-label={label}
            tabIndex={-1}
            onKeyDown={onMenuKeyDown}
          >
            {header && (
              // `role="none"` keeps it out of the menu's item list: it is a label
              // for the menu, not something to arrow onto.
              <div role="none" className="mb-1 border-b border-[var(--color-border)] px-2.5 pb-2 pt-1.5">
                {header}
              </div>
            )}
            {items.map((item, i) => {
              const danger = item.tone === 'danger';
              const radio = item.checked !== undefined;
              return (
                <button
                  key={item.key}
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  type="button"
                  role={radio ? 'menuitemradio' : 'menuitem'}
                  aria-checked={radio ? item.checked : undefined}
                  disabled={item.disabled}
                  onClick={(event) => handleSelect(item, event.detail === 0)}
                  className={[
                    'flex min-h-[40px] w-full cursor-pointer items-center gap-x-2.5 rounded-lg px-2.5 py-2 text-left text-sm font-medium transition-colors focus:outline-hidden focus:ring-2 focus:ring-[var(--color-accent)] disabled:pointer-events-none disabled:opacity-50',
                    danger
                      ? 'text-[var(--color-danger)] hover:bg-[var(--color-danger-subtle)]'
                      : 'text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]',
                  ].join(' ')}
                >
                  {radio ? (
                    // The check column is always reserved so checked and unchecked
                    // labels align; the glyph itself is decorative (aria-checked
                    // carries the state).
                    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center" aria-hidden="true">
                      {item.checked && <Icon icon="heroicons:check" className="h-4 w-4" />}
                    </span>
                  ) : (
                    item.icon && (
                      <Icon icon={item.icon} className="h-4 w-4 shrink-0" aria-hidden="true" />
                    )
                  )}
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}
